declare global {
    interface Window {
        SafeExamBrowser: {
            security: {
                configKey: string;
                browserExamKey: string;
            };
            /**
             * Isolated-clipboard store SEB maintains on the page. In "SEB Only"
             * clipboard mode the native `ClipboardEvent.clipboardData` is empty
             * and SEB exposes the pasted text here instead.
             * See https://github.com/SafeExamBrowser/seb-win-refactoring/blob/master/SafeExamBrowser.Browser/Content/Clipboard.js.
             */
            clipboard?: {
                text: string;
            };
        };
    }
}

/**
 * Memomized first API request origin inside seb browser.
 * Used for continuous requests to match with configKey.
 */
let sebRequestedFromMemo: string = null;

export const isSafeExamBrowser = (): boolean => {
    return typeof window?.SafeExamBrowser?.security?.configKey !== 'undefined';
};

export interface SafeExamBrowserVersion {
    major: number;
    minor: number;
    patch: number;
}

/**
 * Reads the SEB version from the browser User-Agent.
 *
 * SEB appends a `SEB/<version>` token (e.g. `SEB/3.7.0`) to the user agent of
 * the page it loads. Returns `null` when no such token is present — the page is
 * not running inside SEB, or the running SEB does not expose a version.
 */
export const getSafeExamBrowserVersion = (): SafeExamBrowserVersion | null => {
    const match = navigator.userAgent.match(/\bSEB\/(\d+)\.(\d+)(?:\.(\d+))?/);
    if (match === null) {
        return null;
    }

    return {
        major: Number(match[1]),
        minor: Number(match[2]),
        patch: match[3] !== undefined ? Number(match[3]) : 0,
    };
};

/**
 * Checks whether the running SEB reports a version below `major.minor` in the
 * User-Agent. Returns `false` when no SEB version can be determined.
 */
export const isSafeExamBrowserVersionBelow = (
    major: number,
    minor: number,
): boolean => {
    const version = getSafeExamBrowserVersion();
    if (version === null) {
        return false;
    }

    if (version.major !== major) {
        return version.major < major;
    }

    return version.minor < minor;
};

/**
 * SEB fixed the isolated-clipboard paste bug (no `input` event on paste/cut,
 * broken `contenteditable` insertion) in version 3.11.
 * See https://github.com/SafeExamBrowser/seb-win-refactoring/pull/1483.
 *
 * Returns `true` when the running SEB predates that fix and still needs the
 * client-side clipboard workaround. This is the single source of truth for the
 * version gate shared by all paste workarounds.
 */
export const hasSafeExamBrowserClipboardPasteBug = (): boolean => {
    return isSafeExamBrowserVersionBelow(3, 11);
};

/**
 * Returns the text SEB placed in its isolated clipboard, or `''` when running
 * outside SEB or when the clipboard is empty.
 */
export const getSafeExamBrowserClipboardText = (): string => {
    return window?.SafeExamBrowser?.clipboard?.text ?? '';
};

export const getSafeExamBrowserBrowserExamKey = (): string | null => {
    if (isSafeExamBrowser()) {
        return window.SafeExamBrowser.security.browserExamKey ?? null;
    }
    return null;
};

export const getSafeExamBrowserHttpHeaders = () => {
    const headers = {};

    if (isSafeExamBrowser()) {
        // configKey is created based on the URL, but not updated during navigation inside
        // the SPA without a hard-page refresh. Therefore we also safe the url of our
        // first requests to make the behaviour between cacheKey and request url match
        // and the validation in the backend to pass.
        sebRequestedFromMemo = sebRequestedFromMemo ?? window.location?.href;

        headers['X-smartlearn-seb-config-key'] =
            window.SafeExamBrowser.security.configKey;
        headers['X-smartlearn-seb-browser-exam-key'] =
            window.SafeExamBrowser.security.browserExamKey;
        headers['X-smartlearn-seb-requested-from'] = sebRequestedFromMemo;
    }

    return headers;
};

interface PassSebRequiredProps {
    sebRequired?: boolean;
    event: {
        sebRequired: boolean;
    };
}

/**
 * Checks if Safe Exam Browser (SEB) is required for the exam pass but not currently running.
 */
export const checkIsSebRequiredButNotRunning = (
    pass: PassSebRequiredProps,
): boolean => {
    return isSebRequiredActiveOnPass(pass) && !isSafeExamBrowser();
};

const isSebRequiredActiveOnPass = (pass: PassSebRequiredProps): boolean => {
    if (pass.sebRequired) {
        return true;
    }
    return pass.sebRequired === null && pass.event?.sebRequired;
};
